/**
 * 错误呈现契约（CONSTITUTION D1 / RULES §11.2）。
 *
 * 后端统一错误信封：`{ error: { code, message, details, request_id } }`。
 * `client.ts` 已把它解包成 `ApiError`；这里负责把它翻译成**可行动**的界面信息：
 * 发生了什么（message）· 为什么（code）· 下一步怎么做（action）· 如何回溯（requestId）。
 *
 * 禁止在这里返回任何兜底业务数据——错误就是错误。
 */

import { ApiError } from "./client";

export interface ApiErrorView {
  code: string;
  message: string;
  /** 用户可执行的下一步；无建议时为空串。 */
  action: string;
  /** 用于追溯（宪法 A8 / B6），展示在错误态次要位置。 */
  requestId: string | null;
  /** 是否值得重试（网络抖动类为 true，语义冲突类为 false）。 */
  retryable: boolean;
}

const ACTIONS: Record<string, string> = {
  // 以下三条是 2026-09-29 补的：405 方法不匹配 / 未捕获异常 500 / GATE_NOT_APPROVABLE。
  // 前两条此前**不走信封**（纯文本或 `{"detail": …}`），前端只能压成 `HTTP_ERROR` +
  // 状态短语；信封生效后它们真的会出现，故补齐 action。第三条（G-1 专用批准入口）
  // 早就存在，只是 `ACTIONS` 一直没收录——没有条目时 `action` 是空串，用户只看到一句原因。
  METHOD_NOT_ALLOWED: "该操作方式不被支持。请刷新页面重试；若持续出现，请附带请求 ID 反馈。",
  INTERNAL_ERROR: "服务端出现未预期的异常。请稍后重试；若持续出现，请附带请求 ID 反馈。",
  GATE_NOT_APPROVABLE: "该关口有专用批准入口。请到「分类向导」完成 Profile 锁定。",
  MODEL_SETTINGS_LOCAL_ONLY: "模型配置仅允许本机访问。请确认后端在本机运行并由应用自身发起请求。",
  CREDENTIAL_MISSING: "尚未配置 API Key。请到「系统 · 模型配置」填入后重试。",
  CREDENTIAL_STORE_UNAVAILABLE: "系统凭据库不可用，无法安全保存密钥。请检查系统钥匙串权限后重试。",
  MODEL_PROVIDER_MISCONFIGURED:
    "模型网关配置不完整。请到「系统 · 模型配置」补全 base_url 与模型名。",
  MODEL_PROVIDER_RETRYABLE: "模型服务暂时不可用。稍后重试，或检查网络与配额。",
  MODEL_CALL_FAILED: "模型调用失败。请检查模型配置与供应商状态后重试。",
  MODEL_BAD_RESPONSE: "模型返回内容无法解析。可在生成中心对该运行执行重试。",
  MODEL_RESPONSE_UNUSABLE: "模型输出未通过 Schema 校验。请在生成中心重试该运行。",
  MODEL_STRUCTURE_FAILED: "模型输出结构不符合要求。请在生成中心重试该运行。",
  MODEL_SETTINGS_CONFLICT: "模型配置已被其他窗口修改（revision 冲突）。请刷新后重新提交。",
  MODEL_SETTINGS_INVALID: "模型配置字段不合法。请按提示修正后重试。",
  MISSING_IDEMPOTENCY_KEY: "生成请求缺少幂等键。请重新发起生成。",
  QUOTA_EXCEEDED: "额度已超限。请在项目总览查看用量，或调整预算后再执行。",
  CAPABILITY_FORBIDDEN: "当前能力模块无权写入该产物类型。请更换能力模块，或改由有权模块执行。",
  GATE_NOT_PASSED: "上游关口尚未通过。请先到「审核关口」完成对应批准。",
  ARTIFACT_LOCKED: "该产物已批准锁定，不可直接修改。修改须创建新版本并经真人批准。",
  PROFILE_BLOCKED: "Profile 尚未完成或存在适配器冲突。请先到「分类向导」处理。",
  ADAPTER_CONFLICT: "适配器对同一字段给出冲突值。请到「分类向导」人工裁决。",
  METHODOLOGY_LOCKED: "方法论版本已锁定。升级需先运行影响预览并人工批准。",
  SCHEMA_INVALID: "请求内容不符合 Schema。请检查字段后重试。",
  VALIDATION_FAILED: "输入校验未通过。请按提示修正后重试。",
  EXPORT_FILE_MISSING: "交付文件已不在服务器上（可能被清理或迁移）。请联系管理员重新导出。",
  NOT_FOUND: "目标不存在，可能已被删除或不属于当前项目。",
  HTTP_ERROR: "",
};

const RETRYABLE_CODES = new Set([
  "MODEL_PROVIDER_RETRYABLE",
  "MODEL_CALL_FAILED",
  "MODEL_BAD_RESPONSE",
  "MODEL_RESPONSE_UNUSABLE",
  "MODEL_STRUCTURE_FAILED",
  "HTTP_ERROR",
]);

export function describeApiError(error: unknown): ApiErrorView {
  if (error instanceof ApiError) {
    return {
      code: error.code,
      message: error.message,
      action: ACTIONS[error.code] ?? "",
      requestId: error.requestId,
      retryable: RETRYABLE_CODES.has(error.code) || error.status >= 500,
    };
  }
  if (error instanceof Error) {
    return {
      code: "UNEXPECTED",
      message: error.message,
      action: "请刷新页面重试；若持续出现，请附带请求 ID 反馈。",
      requestId: null,
      retryable: true,
    };
  }
  return {
    code: "UNEXPECTED",
    message: "发生未知错误",
    action: "请刷新页面重试。",
    requestId: null,
    retryable: true,
  };
}
